import debugx from "debugx";
import { Position } from "../types";
import { BaseTrackingProvider, TrackingData } from "./trackingProvider";
import {
  getSimulationService,
  SimulationService,
  SimulationOptions,
  SIMULATION_ROUTES,
} from "./simulationService";

const debug = debugx("bikedash:simulation-tracking");

export interface SimulationConfig {
  routeId?: string;
  options?: Partial<SimulationOptions>;
}

export class SimulationTrackingProvider extends BaseTrackingProvider {
  readonly id = "simulation";
  readonly name = "GPS Simulation";
  readonly type = "simulation" as const;

  private simulationService: SimulationService;

  constructor() {
    super();
    this.simulationService = getSimulationService();
    debug("Simulation tracking provider initialized");
  }

  async isAvailable(): Promise<boolean> {
    return true; // Simulation is always available
  }

  async start(): Promise<void> {
    if (this._isActive) {
      debug.warn("Simulation tracking already active");
      return;
    }

    // Set up position callback to forward to subscribers
    this.simulationService.setPositionCallback((position: Position) => {
      const trackingData: TrackingData = {
        position,
        isActive: true,
        source: "simulation",
        metadata: {
          accuracy: position.accuracy,
          heading: position.heading,
          compassHeading: position.compassHeading,
          speed: position.speed,
          timestamp: position.timestamp,
        },
      };

      // Log different types of updates
      if (position.lat && position.lng) {
        debug("Simulation position update:", {
          lat: position.lat.toFixed(6),
          lng: position.lng.toFixed(6),
          speed: position.speed?.toFixed(1),
          heading: position.heading?.toFixed(0),
        });
      } else if (position.compassHeading !== undefined) {
        // Compass-only update - still forward it!
        debug(
          "Simulation compass update: %s°",
          position.compassHeading.toFixed(1),
        );
      }

      // Forward ALL updates, including compass-only
      this.notifySubscribers(trackingData);
    });

    // If no route is set, use the first available route
    if (!this.simulationService.getCurrentRoute()) {
      const defaultRoute = SIMULATION_ROUTES[0];
      if (defaultRoute) {
        this.simulationService.setRoute(defaultRoute.id);
        debug("Set default simulation route:", defaultRoute.name);
      }
    }

    const success = this.simulationService.start();
    if (!success) {
      throw new Error("Failed to start GPS simulation");
    }

    this._isActive = true;
    debug("Simulation tracking started");
  }

  stop(): void {
    if (!this._isActive) return;

    this.simulationService.stop();
    this._isActive = false;

    const stoppedData: TrackingData = {
      position: null,
      isActive: false,
      source: "simulation",
    };
    this.notifySubscribers(stoppedData);

    debug("Simulation tracking stopped");
  }

  pause(): void {
    if (!this._isActive) return;

    this.simulationService.pause();
    debug("Simulation tracking paused/resumed");
  }

  resume(): void {
    // The simulation service's pause() method is a toggle
    this.pause();
  }

  configure(config: SimulationConfig): void {
    debug("Configuring simulation:", config);

    if (config.routeId) {
      const success = this.simulationService.setRoute(config.routeId);
      if (!success) {
        debug.warn("Failed to set simulation route:", config.routeId);
      }
    }

    if (config.options) {
      this.simulationService.setOptions(config.options);
    }
  }

  getConfiguration(): SimulationConfig {
    const currentRoute = this.simulationService.getCurrentRoute();
    const simState = this.simulationService.getState();

    return {
      routeId: currentRoute?.id,
      options: simState.simulationOptions,
    };
  }

  getSimulationState() {
    return this.simulationService.getState();
  }

  getProgress() {
    return this.simulationService.getProgress();
  }

  getAvailableRoutes() {
    return this.simulationService.getAvailableRoutes();
  }

  getCurrentRoute() {
    return this.simulationService.getCurrentRoute();
  }

  destroy(): void {
    debug("Destroying simulation tracking provider");
    this.stop();
    this.simulationService.setPositionCallback(() => {});
    super.destroy();
  }
}
